import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, Diamond, Flame, Shield, Zap, Clock, XCircle } from "lucide-react";
import bundleDobleRevolucion from "@/assets/bundle-doble-revolucion.png";
import bonusRutaDinero from "@/assets/bonus-ruta-dinero.png";
import bonusEscudoLegal from "@/assets/bonus-escudo-legal.png";
import bonusLimpiezaRastro from "@/assets/bonus-limpieza-rastro.png";
import bonusDirectorio from "@/assets/bonus-directorio-brokers.png";
import bonusPiggybacking from "@/assets/bonus-piggybacking.png";
import bonusBancosQuiebra from "@/assets/bonus-bancos-quiebra.png";
import { useHotmartIframeFix } from "@/hooks/use-hotmart-iframe-fix";
import LazyVideo from "@/components/landing/LazyVideo";
const Bundle = () => {
  useHotmartIframeFix("hotmart-sales-funnel-bundle", {
    minHeightDesktop: 260,
    maxHeightDesktop: 300,
    viewportFillDesktop: 0.28,
    minHeightMobile: 260,
    maxHeightMobile: 300,
    viewportFillMobile: 0.32,
  });
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds

  useEffect(() => {
    // Load Hotmart checkout script
    const script = document.createElement('script');
    script.src = 'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js';
    script.async = true;
    script.onload = () => {
      if ((window as any).checkoutElements) {
        (window as any).checkoutElements.init('salesFunnel').mount('#hotmart-sales-funnel-bundle');
      }
    };
    document.body.appendChild(script);
    return () => {
      const existingScript = document.querySelector('script[src="https://checkout.hotmart.com/lib/hotmart-checkout-elements.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <main className="min-h-screen bg-gradient-to-b from-[#0a0a0a] via-[#111111] to-[#0a0a0a] text-white">
      {/* Barra Superior Parpadeante */}
      <div className="bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 py-4 px-4 text-center relative overflow-hidden animate-pulse">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <p className="text-lg md:text-xl font-black text-slate-900 relative z-10">
          🚨 ¡ALTO! ÚLTIMA OPORTUNIDAD PARA TRANSFORMAR TU CRÉDITO PARA SIEMPRE. ¡NO CERRAR ESTA PÁGINA!
        </p>
        <div className="max-w-xs mx-auto mt-3 relative z-10">
          <div className="h-3 bg-black/20 rounded-full overflow-hidden border border-white/20">
            <div className="h-full w-full bg-gradient-to-r from-teal-300 via-cyan-200 to-amber-300 rounded-full"></div>
          </div>
          <p className="text-xs mt-1 text-slate-800 font-bold">OFERTA 100% COMPLETA - ÚLTIMO PASO</p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,215,0,0.1)_0%,_transparent_70%)]"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Pre-título */}
          <p className="text-red-500 font-black text-xl md:text-2xl mb-4 animate-pulse">
            ¡ESTO NUNCA VOLVERÁ A SUCEDER!
          </p>
          
          {/* Título Principal */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
              LA DOBLE REVOLUCIÓN
            </span>
            <br />
            <span className="text-white">
              DE TU CRÉDITO:
            </span>
            <br />
            <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
              ACELERADOR + FÉNIX
            </span>
          </h1>
          
          {/* Subtítulo */}
          <p className="text-lg md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12">
            Consigue <strong className="text-yellow-400">AÑOS de antigüedad</strong> y borra para siempre el{" "}
            <strong className="text-yellow-400">estigma de la bancarrota</strong>.{" "}
            <span className="text-red-400 font-bold">¡Los 2 productos más potentes por el precio de uno!</span>
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-8 px-4">
        <div className="max-w-sm mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-500/50">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-cyan-400/20 blur-xl -z-10"></div>
            <LazyVideo 
              className="w-full aspect-[9/16]" 
              poster="/images/video-poster-bundle.jpg"
              src="/videos/video-bundle.mp4"
            />
          </div>
        </div>
      </section>

      {/* Bundle Visual Section */}
      <section className="py-12 px-4 relative">
        <div className="max-w-4xl mx-auto">
          <div className="relative flex justify-center items-center">
            {/* Halo Effect */}
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="w-[600px] h-[600px] bg-gradient-to-r from-cyan-500/30 via-blue-500/20 to-cyan-500/30 rounded-full blur-3xl animate-pulse"></div>
            </div>
            
            {/* Bundle Image */}
            <div className="relative z-10 hover:scale-105 transition-transform duration-300">
              <img src={bundleDobleRevolucion} alt="La Doble Revolución - Paquete Master" className="w-full max-w-2xl drop-shadow-[0_0_40px_rgba(0,200,255,0.4)]" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Problem 1 */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-900/50 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-red-400">Problema #1</h3>
              </div>
              <p className="text-gray-300">
                <strong className="text-white">Cansado de esperar 5-10 años</strong> para un buen score de crédito mientras otros disfrutan oportunidades.
              </p>
            </div>

            {/* Problem 2 */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-900/50 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-red-400">Problema #2</h3>
              </div>
              <p className="text-gray-300">
                <strong className="text-white">Atrapado con el estigma de la bancarrota</strong> sin esperanza de conseguir crédito, auto o casa.
              </p>
            </div>

            {/* Solution */}
            <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 rounded-2xl p-6 border-2 border-yellow-500/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400/10 rounded-full blur-2xl"></div>
              <div className="flex items-center gap-3 mb-4 relative z-10">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <h3 className="text-lg font-bold text-yellow-400">LA SOLUCIÓN</h3>
              </div>
              <p className="text-gray-200 relative z-10">
                <strong className="text-yellow-300">Reconstrucción financiera TOTAL y RÁPIDA</strong> con la combinación perfecta de ambos sistemas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-12">
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
              LO QUE INCLUYE EL BUNDLE COMPLETO
            </span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Acelerador Block */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-yellow-500/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-4 mb-6 relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
                  <Diamond className="w-8 h-8 text-black" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-yellow-400">EL ACELERADOR</h3>
                  <p className="text-gray-400">DE DIAMANTE</p>
                </div>
              </div>
              <ul className="space-y-4 relative z-10">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Añade antigüedad y límites altos</strong> al instante</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Directorio de Brokers Confiables</strong> verificados</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Estrategias de Aplicación</strong> Inteligente</span>
                </li>
              </ul>
              <p className="mt-6 text-right text-gray-500 line-through">Valor: $67</p>
            </div>

            {/* Fénix Block */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-orange-500/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-4 mb-6 relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Flame className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-orange-400">FÉNIX</h3>
                  <p className="text-gray-400">FINANCIERO</p>
                </div>
              </div>
              <ul className="space-y-4 relative z-10">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Borrando el rastro</strong> de la bancarrota</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Lista de Bancos "Friendly"</strong> que sí aprueban</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200"><strong className="text-white">Guía para autos/casas</strong> post-quiebra</span>
                </li>
              </ul>
              <p className="mt-6 text-right text-gray-500 line-through">Valor: $97</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-transparent via-red-950/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 text-slate-900 text-sm font-bold px-6 py-2 rounded-full mb-4 animate-pulse">
              🎁 ¡Y ESO NO ES TODO!
            </span>
            <h2 className="text-3xl md:text-4xl font-black">
              <span className="text-white">LLÉVATE ESTOS </span>
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                6 BONOS EXCLUSIVOS
              </span>
              <span className="text-white"> CON EL PAQUETE DOBLE</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Bonus 1 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img src={bonusDirectorio} alt="Directorio de Brokers" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" loading="lazy" decoding="async" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">Directorio de Brokers</h4>
              <p className="text-gray-400 text-sm mb-3">
                Lista exclusiva de brokers verificados para añadir tradelines de confianza.
              </p>
              <p className="text-green-400 font-bold">Valor: $97 - GRATIS</p>
            </div>

            {/* Bonus 2 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img src={bonusPiggybacking} alt="Estrategia Piggybacking" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" loading="lazy" decoding="async" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">Estrategia Piggybacking</h4>
              <p className="text-gray-400 text-sm mb-3">
                El multiplicador de score que te permite "montarte" en el historial de otros.
              </p>
              <p className="text-green-400 font-bold">Valor: $67 - GRATIS</p>
            </div>

            {/* Bonus 3 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img src={bonusRutaDinero} alt="La Ruta del Dinero" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" loading="lazy" decoding="async" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">La Ruta del Dinero</h4>
              <p className="text-gray-400 text-sm mb-3">
                El mapa exacto para saber dónde aplicar y maximizar tu score inflado.
              </p>
              <p className="text-green-400 font-bold">Valor: $47 - GRATIS</p>
            </div>

            {/* Bonus 4 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img src={bonusBancosQuiebra} alt="Bancos Amigos de la Quiebra" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" loading="lazy" decoding="async" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">Bancos Amigos de la Quiebra</h4>
              <p className="text-gray-400 text-sm mb-3">
                Lista de bancos que SÍ aprueban después de bancarrota.
              </p>
              <p className="text-green-400 font-bold">Valor: $47 - GRATIS</p>
            </div>

            {/* Bonus 5 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img alt="Limpieza de Rastro Público" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" src="/lovable-uploads/0eea685b-f082-430c-bd62-518b83a1b52b.png" loading="lazy" decoding="async" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">Limpieza de Rastro Público</h4>
              <p className="text-gray-400 text-sm mb-3">
                Técnicas avanzadas para borrar tu huella digital negativa.
              </p>
              <p className="text-green-400 font-bold">Valor: $67 - GRATIS</p>
            </div>

            {/* Bonus 6 */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur rounded-2xl p-6 border border-yellow-500/30 text-center hover:scale-105 transition-transform duration-300">
              <img alt="El Escudo Legal" className="w-36 h-36 mx-auto object-contain mb-4 drop-shadow-[0_0_20px_rgba(255,215,0,0.3)]" src="/lovable-uploads/5f38eef3-cfb1-46fc-bd9e-02e0a327055d.png" />
              <h4 className="font-bold text-yellow-400 text-lg mb-2">El Escudo Legal</h4>
              <p className="text-gray-400 text-sm mb-3">
                Guía de Protección Jurídica para blindar tu recuperación financiera.
              </p>
              <p className="text-green-400 font-bold">Valor: $97 - GRATIS</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Countdown Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {/* Countdown Timer */}
          <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 rounded-2xl p-6 mb-10 border-2 border-transparent bg-clip-padding" style={{
          backgroundImage: 'linear-gradient(to right, rgb(30 41 59 / 0.8), rgb(15 23 42 / 0.8)), linear-gradient(to right, rgb(20 184 166), rgb(34 211 238), rgb(245 158 11))',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}>
            <div className="flex items-center justify-center gap-3 mb-2">
              <Clock className="w-6 h-6 text-cyan-400 animate-pulse" />
              <p className="text-cyan-400 font-bold text-lg">¡ESTA OFERTA EXPIRA EN!</p>
            </div>
            <div className="text-5xl md:text-7xl font-black bg-gradient-to-r from-teal-400 via-cyan-300 to-amber-400 bg-clip-text text-transparent font-mono">
              {formatTime(timeLeft)}
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-8">
            <p className="text-gray-500 text-lg mb-2">Precio Regular por separado:</p>
            <p className="text-4xl font-bold text-gray-500 line-through">$164</p>
          </div>

          <div className="mb-8">
            <p className="text-yellow-400 text-xl font-bold mb-2">
              HOY, SOLO EN ESTA PÁGINA, consigue ambos por solo...
            </p>
            <div className="relative inline-block">
              <p className="text-7xl md:text-9xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                $14.97
              </p>
              <div className="absolute -top-4 -right-12 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full rotate-12">
                ¡AHORRA +$149!
              </div>
            </div>
            <p className="text-gray-400 mt-2">Pago único • Acceso de por vida</p>
          </div>

          {/* CTA ya está abajo, widget movido al footer */}

          {/* CTA Button */}
          <Button asChild className="w-full md:w-auto bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 hover:from-teal-400 hover:via-cyan-300 hover:to-amber-400 text-slate-900 font-black text-lg md:text-2xl py-8 px-12 h-auto rounded-xl shadow-[0_0_60px_rgba(20,184,166,0.5)] hover:shadow-[0_0_80px_rgba(20,184,166,0.7)] transition-all duration-300 transform hover:scale-105">
            <a href="/gracias">
              <span className="whitespace-normal text-center leading-tight">
                SÍ, QUIERO EL PAQUETE DOBLE Y AHORRAR MÁS DEL 80% HOY
              </span>
            </a>
          </Button>

          {/* No Link */}
          <div className="mt-8">
            <a href="/gracias" className="text-sm text-gray-600 hover:text-gray-400 underline transition-colors">
              No, gracias. Prefiero comprar estos productos individualmente después a su precio completo.
            </a>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border-2 border-transparent bg-clip-padding" style={{
          backgroundImage: 'linear-gradient(to right, rgb(17 24 39), rgb(31 41 55)), linear-gradient(to right, rgb(20 184 166), rgb(34 211 238), rgb(245 158 11))',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}>
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-teal-500 via-cyan-400 to-amber-500 rounded-full flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(20,184,166,0.4)]">
              <Shield className="w-12 h-12 text-slate-900" />
            </div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-400 via-cyan-300 to-amber-400 bg-clip-text text-transparent mb-3">Garantía de 30 Días</h3>
            <p className="text-gray-400">
              Si por cualquier razón no estás 100% satisfecho, te devolvemos cada centavo. Sin preguntas, sin complicaciones.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800 bg-black/50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="mb-6 text-muted-foreground font-extrabold text-xl">
            © {new Date().getFullYear()} Universo Digital Academy. Todos los derechos reservados.
          </p>
        </div>

        {/* Hotmart Sales Funnel Widget (full-bleed) */}
        <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
          <div id="hotmart-sales-funnel-bundle" className="w-full" />
        </div>
      </footer>
    </main>;
};
export default Bundle;